/**
 * DreamBlock — the dash-through corridor block (Brief 014). Celeste's signature: a
 * block that is SOLID normally but PASS-THROUGH while the player is dashing, so a dash
 * carries straight through it.
 *
 * Reuses the conditional-`solidBody` idiom (the same hook a broken Brick uses to stop
 * colliding): the block returns null while the player's dash is live, else its AABB.
 * The dash state is cached in `update` (which runs before World's solid pass) so the
 * solid resolution sees the right value this step.
 *
 * On a non-dash archetype `player.dashActiveSteps` is always 0 → the block is just a
 * permanent solid (a harmless, recognizable wall). Determinism: a boolean read of an
 * integer counter, no math → ESLint-clean under src/engine/.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

export class DreamBlock implements Entity {
  readonly type = EntityType.DREAMBLOCK;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private dashing = false;

  /** @param props `w`/`h` in tiles (default 2×2). Extends right + down from the placed cell. */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    const w = posInt(props?.w, 2);
    const h = posInt(props?.h, 2);
    this.body = { x: cx - ts / 2, y: feetY - ts, w: w * ts, h: h * ts };
  }

  /** Solid unless the player is mid-dash (then pass-through). */
  solidBody(): AABB | null { return this.dashing ? null : this.body; }

  update(_dt: number, ctx: EntityContext): void {
    // Cache the dash state for this step's solid pass (which has no ctx).
    this.dashing = ctx.player.dashActiveSteps > 0;
  }

  onPlayerOverlap(_ctx: EntityContext): void { /* solid — acts via solidBody */ }

  spriteKey(): string { return 'dreamblock'; }

  visible(): boolean { return true; }

  renderSize(): { w: number; h: number } { return { w: this.body.w, h: this.body.h }; }
}

function posInt(v: unknown, dflt: number): number {
  return typeof v === 'number' && v >= 1 ? Math.floor(v) : dflt;
}
